import React, { JSX } from 'react'
import { Link } from '@/i18n/navigation'

/**
 * Props for link components
 */
export interface LinkComponentProps {
  href?: string
  target?: '_blank' | '_self' | '_parent' | '_top'
  rel?: string
}

/**
 * Custom hook that returns the appropriate component based on the provided element type.
 * If a component is provided, it returns that component.
 * Otherwise, it returns the HTML element type as a string.
 * For links with href, it automatically uses the internationalized Link component.
 *
 * @param elementType - The element type to render
 * @param href - Optional href to determine if this should be a link
 * @returns The resolved component to render
 */
export const useLink = (
  elementType: React.ElementType | keyof JSX.IntrinsicElements,
  href?: string
): React.ElementType => {
  // If href is provided and elementType is default 'button', use the internationalized Link
  if (href && elementType === 'button') {
    return Link
  }

  // If elementType is a component (function or class), return it directly
  if (typeof elementType !== 'string') {
    return elementType
  }

  // Otherwise, return the string element type
  return elementType
}
